import { Temporal } from '@js-temporal/polyfill'
import type { Recording, Segment } from './types'

export function dayBounds(day: string, timezone: string) {
  const date = Temporal.PlainDate.from(day)
  return {
    start: date.toZonedDateTime(timezone).epochMilliseconds,
    end: date.add({ days: 1 }).toZonedDateTime(timezone).epochMilliseconds,
  }
}
export function localDay(ms: number, timezone: string) {
  return Temporal.Instant.fromEpochMilliseconds(Math.round(ms))
    .toZonedDateTimeISO(timezone)
    .toPlainDate()
    .toString()
}
export function onDay(ms: number, day: string, timezone: string) {
  const time = Temporal.Instant.fromEpochMilliseconds(Math.round(ms))
    .toZonedDateTimeISO(timezone)
    .toPlainTime()
  return Temporal.PlainDate.from(day)
    .toPlainDateTime(time)
    .toZonedDateTime(timezone, { disambiguation: 'compatible' }).epochMilliseconds
}
export function addDays(day: string, count: number) {
  return Temporal.PlainDate.from(day).add({ days: count }).toString()
}
export function monthOf(day: string) {
  return day.slice(0, 7)
}
export function timeLabel(ms: number, timezone: string, seconds = false, zone = false) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    ...(seconds ? { second: '2-digit' } : {}),
    hourCycle: 'h23',
    ...(zone ? { timeZoneName: 'short' } : {}),
  }).format(ms)
}
export function dateLabel(day: string) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${day}T12:00:00Z`))
}
export function wallToMedia(r: Recording, utc: number) {
  return (
    (r.mediaStart + ((utc - r.start) * (r.mediaEnd - r.mediaStart)) / (r.end - r.start)) /
    1000
  )
}
export function mediaToWall(r: Recording, seconds: number) {
  return (
    r.start +
    ((seconds * 1000 - r.mediaStart) * (r.end - r.start)) / (r.mediaEnd - r.mediaStart)
  )
}
export function speedFactor(r: Recording) {
  return (r.end - r.start) / (r.mediaEnd - r.mediaStart)
}

// Full-quality footage wins, then configured source order. Within a source,
// prefer later starts, prepared assets with previews, then stable recording IDs.
export function buildSegments(
  recordings: Recording[],
  sourceIds: string[] = [],
): Segment[] {
  const unique = [...new Map(recordings.map((r) => [r.id, r])).values()].filter(
    (r) => r.end > r.start && r.mediaEnd > r.mediaStart,
  )
  const boundaries = [...new Set(unique.flatMap((r) => [r.start, r.end]))].sort(
    (a, b) => a - b,
  )
  const ranks = new Map(sourceIds.map((id, index) => [id, index]))
  const priority = (a: Recording, b: Recording) =>
    Number(a.playbackKind === 'archive') - Number(b.playbackKind === 'archive') ||
    (ranks.get(a.cameraId) ?? sourceIds.length) -
      (ranks.get(b.cameraId) ?? sourceIds.length) ||
    b.start - a.start ||
    Number(b.prepared) - Number(a.prepared) ||
    Number(Boolean(b.previewId)) - Number(Boolean(a.previewId)) ||
    a.id.localeCompare(b.id)
  const sorted = unique.sort(priority)
  const segments: Segment[] = []
  for (let i = 0; i < boundaries.length - 1; i++) {
    const start = boundaries[i],
      end = boundaries[i + 1]
    const recording = sorted.find((r) => r.start <= start && r.end >= end)
    if (!recording) continue
    const last = segments.at(-1)
    if (last?.recording.id === recording.id && last.end === start) last.end = end
    else segments.push({ start, end, recording })
  }
  return segments
}

export function locate(
  segments: Segment[],
  utc: number,
): { segment: Segment; utc: number; gap: boolean } | null {
  const exact = segments.find((s) => utc >= s.start && utc < s.end)
  if (exact) return { segment: exact, utc, gap: false }
  const next = segments.find((s) => s.start >= utc)
  if (next) return { segment: next, utc: next.start, gap: true }
  const last = segments.at(-1)
  return last ? { segment: last, utc: last.end - 1, gap: true } : null
}

export function nextSegment(segments: Segment[], segment: Segment) {
  return segments.find((s) => s.start >= segment.end) ?? null
}

export function sampleAt<T extends { media_ms: number }>(
  samples: T[],
  mediaMs: number,
): T | null {
  if (!samples.length) return null
  let left = 0,
    right = samples.length - 1
  while (left < right) {
    const mid = Math.ceil((left + right) / 2)
    if (samples[mid].media_ms <= mediaMs) left = mid
    else right = mid - 1
  }
  return samples[left]
}
